import cx from "clsx";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ThemeProvider from "kreate-common/modules/kreate-ui/components/ThemeProvider";
import React from "react";
import { MdSell } from "react-icons/md";

import Layout from "../PageGalleryCollector/components/Layout";
import GallerySidebar from "../PageGalleryCollector/containers/GallerySidebar";
import { useGallerySidebarApi } from "../PageGalleryCollector/containers/GallerySidebar/hooks/useGallerySidebarApi";
import SectionHeadline from "../PageGalleryCollector/containers/SectionHeadline";

import SentOfferViewer from "./components/SentOfferViewer";
import styles from "./index.module.scss";
import { getPageTitle } from "./utils";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { getThumbnailUrlFromBlobId } from "@/modules/kreate-components/components/ImageViewer/utils";
import KreateHead from "@/modules/kreate-components/components/KreateHead";
import NavbarV4 from "@/modules/kreate-components/components/NavbarV4";
import StatusIndicatorViewer from "@/modules/kreate-components/components/StatusIndicatorViewer";
import { useThemeFromSessionPreferences } from "@/modules/kreate-components/hooks/useThemeFromSessionPreferences";
import { useCommandResultInfinite$GetGalleryOfferList } from "@/modules/resources/hooks/useCommandResultInfinite";
import { useGalleryCollector$GivenAddressIsh } from "@/modules/resources/hooks/useGalleryCollector$GivenAddressIsh";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  addressIsh: string;
};

export default function PageGalleryCollector$SentOffers({
  className,
  style,
  addressIsh,
}: Props) {
  const loginStatus = useLoginStatus();
  const fromSessionPreferences = useThemeFromSessionPreferences();
  const [galleryCollector] = useGalleryCollector$GivenAddressIsh(addressIsh);
  const galleryInfo = galleryCollector?.populated?.galleryInfo;

  const gallerySidebarApi = useGallerySidebarApi({
    galleryCollector,
    sidebarLocation: { type: "sent-offers" },
    addressIsh,
  });

  const resource = useCommandResultInfinite$GetGalleryOfferList({
    listType: "sent",
    addressOrHandle: addressIsh,
    offset: 0,
    limit: 12,
  });

  const isLoggedInUserGalleryOwner =
    !!loginStatus?.loggedIn &&
    loginStatus.userId === galleryCollector?.populated?.userProfile?.userId;

  return (
    <ThemeProvider theme={fromSessionPreferences.theme} asChild>
      <Layout.Root className={cx(styles.container, className)} style={style}>
        <KreateHead
          title={getPageTitle({ galleryInfo, addressIsh })}
          description={galleryInfo?.description}
          imageUrl={
            galleryInfo?.avatar
              ? getThumbnailUrlFromBlobId(galleryInfo.avatar.blob.blobId)
              : undefined
          }
          url={undefined}
        />
        <Layout.Header>
          <NavbarV4
            product="market"
            hideRibbonCarousel
            makeTransparentUnlessScrolledDown
          />
        </Layout.Header>
        <Layout.BeforeMain>
          <SectionHeadline
            galleryCollector={galleryCollector}
            addressIsh={addressIsh}
          />
        </Layout.BeforeMain>
        <Layout.Main>
          <Layout.Left>
            <GallerySidebar api={gallerySidebarApi} />
          </Layout.Left>
          <Layout.Center className={styles.center}>
            <Flex.Col className={styles.center} padding="32px 24px" gap="20px">
              <h2 className={styles.title}>
                <Flex.Row alignItems="center" gap="8px">
                  <MdSell size="24px" />
                  <span
                    title={formatInteger$Standard(resource.data?.[0]?.count)}
                  >
                    {[
                      "Sent Offers (",
                      formatInteger$Compact(resource.data?.[0]?.count),
                      ")",
                    ].join("")}
                  </span>
                </Flex.Row>
              </h2>
              <div className={styles.grid}>
                {resource.data
                  ?.flatMap((d) => d.offers)
                  .map((offer) => (
                    <SentOfferViewer
                      key={offer.utxo.txId}
                      className={styles.cell}
                      offer={offer}
                      isLoggedInUserGalleryOwner={isLoggedInUserGalleryOwner}
                    />
                  ))}
              </div>
              <Flex.Row
                alignItems="center"
                justifyContent="center"
                minHeight="96px"
              >
                <StatusIndicatorViewer
                  statusIndicator={resource.statusIndicator}
                  onClickLoadMore={resource.loadMore}
                />
              </Flex.Row>
            </Flex.Col>
          </Layout.Center>
        </Layout.Main>
      </Layout.Root>
    </ThemeProvider>
  );
}
